// Reusable environmental props. Every prop returns a themable mesh group.
// Keep polygon counts LOW — most maps place 30-80 props total.
// All props register their own collision AABBs by pushing to the walls array
// passed in via the collectWalls callback (see how maps wire this up).
import * as THREE from "three";
import { MAT } from "./materials";

// -------------- Basic building blocks ---------------------------------------

// Concrete boundary wall panel — used for perimeter, replaces plain box
export function ConcreteWall({ position, size, color = "concrete", stripe = true, shadows = false }) {
    const [sx, sy, sz] = size;
    return (
        <group position={position}>
            <mesh castShadow={shadows} receiveShadow material={MAT[color]()}>
                <boxGeometry args={[sx, sy, sz]} />
            </mesh>
            {stripe && (
                // Weathered stripe at top
                <mesh position={[0, sy * 0.35, sx > sz ? 0 : sx * 0.51]} material={MAT.metalRust()}>
                    <boxGeometry args={[sx > sz ? sx * 0.98 : 0.02, 0.08, sx > sz ? 0.02 : sz * 0.98]} />
                </mesh>
            )}
        </group>
    );
}

// Wooden crate — small tactical cover
export function Crate({ position, rotation = [0, 0, 0], size = [1.6, 1.4, 1.6], shadows = false }) {
    const [sx, sy, sz] = size;
    return (
        <group position={position} rotation={rotation}>
            <mesh castShadow={shadows} receiveShadow material={MAT.wood()}>
                <boxGeometry args={[sx, sy, sz]} />
            </mesh>
            {/* Corner banding */}
            {[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([dx, dz], i) => (
                <mesh key={i} position={[dx * (sx / 2 - 0.02), 0, dz * (sz / 2 - 0.02)]} material={MAT.metalDark()}>
                    <boxGeometry args={[0.06, sy, 0.06]} />
                </mesh>
            ))}
            {/* X planks on top */}
            <mesh position={[0, sy / 2 + 0.01, 0]} rotation={[Math.PI / 2, 0, 0]} material={MAT.woodLight()}>
                <boxGeometry args={[sx * 0.95, 0.02, 0.08]} />
            </mesh>
        </group>
    );
}

// Shipping container — big cover / traversable
export function ShippingContainer({
    position,
    rotation = [0, 0, 0],
    color = "metalRed",
    size = [6, 2.6, 2.4],
    shadows = false,
}) {
    const [sx, sy, sz] = size;
    return (
        <group position={position} rotation={rotation}>
            <mesh castShadow={shadows} receiveShadow material={MAT[color]()}>
                <boxGeometry args={[sx, sy, sz]} />
            </mesh>
            {/* Ridges every 0.5m */}
            {Array.from({ length: 10 }).map((_, i) => (
                <mesh key={i} position={[-sx / 2 + 0.3 + i * (sx / 12), 0, sz / 2 + 0.01]} material={MAT.metalDark()}>
                    <boxGeometry args={[0.04, sy * 0.92, 0.02]} />
                </mesh>
            ))}
            {/* Doors */}
            <mesh position={[sx / 2 + 0.01, 0, 0]} material={MAT.metalDark()}>
                <boxGeometry args={[0.02, sy * 0.98, sz * 0.98]} />
            </mesh>
        </group>
    );
}

// Metal barrel (oil drum)
export function Barrel({ position, color = "metalRed", shadows = false }) {
    return (
        <group position={position}>
            <mesh castShadow={shadows} receiveShadow material={MAT[color]()}>
                <cylinderGeometry args={[0.5, 0.5, 1.1, 12]} />
            </mesh>
            {/* Bands */}
            {[-0.35, 0.35].map((y, i) => (
                <mesh key={i} position={[0, y, 0]} material={MAT.metalDark()}>
                    <torusGeometry args={[0.5, 0.03, 6, 16]} rotation={[Math.PI / 2, 0, 0]} />
                </mesh>
            ))}
        </group>
    );
}

// Street lamp — tall pole with warm bulb (auto-emissive)
export function StreetLamp({ position, height = 5.5, warm = true, shadows = false }) {
    return (
        <group position={position}>
            {/* Pole */}
            <mesh castShadow={shadows} receiveShadow material={MAT.metalDark()}>
                <cylinderGeometry args={[0.07, 0.09, height, 8]} />
            </mesh>
            {/* Arm */}
            <mesh position={[0.6, height / 2 - 0.2, 0]} material={MAT.metalDark()}>
                <boxGeometry args={[1.2, 0.08, 0.08]} />
            </mesh>
            {/* Lamp housing */}
            <mesh position={[1.2, height / 2 - 0.35, 0]} material={MAT.metalDark()}>
                <boxGeometry args={[0.5, 0.2, 0.3]} />
            </mesh>
            {/* Bulb — emissive */}
            <mesh
                position={[1.2, height / 2 - 0.55, 0]}
                material={warm ? MAT.lightWarm() : MAT.lightCool()}
            >
                <boxGeometry args={[0.4, 0.05, 0.28]} />
            </mesh>
            <pointLight
                position={[1.2, height / 2 - 0.55, 0]}
                intensity={warm ? 1.4 : 1.2}
                color={warm ? "#ffb26b" : "#a4d9e6"}
                distance={8}
                decay={2}
            />
        </group>
    );
}

// Chain-link fence panel (thin strip cover — doesn't block bullets narratively
// but here it acts as a soft barrier for path/AI). Height 2, length variable.
export function Fence({ position, rotation = [0, 0, 0], length = 6, shadows = false }) {
    return (
        <group position={position} rotation={rotation}>
            {/* Posts */}
            {Array.from({ length: Math.ceil(length / 2) + 1 }).map((_, i) => {
                const x = -length / 2 + i * 2;
                return (
                    <mesh key={i} position={[x, 1, 0]} castShadow={shadows} material={MAT.metalDark()}>
                        <cylinderGeometry args={[0.05, 0.05, 2, 6]} />
                    </mesh>
                );
            })}
            {/* Mesh panel — flat translucent grid */}
            <mesh position={[0, 1, 0]} material={MAT.metalDark()}>
                <boxGeometry args={[length, 1.9, 0.02]} />
            </mesh>
            {/* Top rail */}
            <mesh position={[0, 2, 0]} material={MAT.metalDark()}>
                <boxGeometry args={[length, 0.05, 0.05]} />
            </mesh>
        </group>
    );
}

// Sandbag stack — desert / military cover
export function Sandbags({ position, rotation = [0, 0, 0], rows = 3, length = 3, shadows = false }) {
    return (
        <group position={position} rotation={rotation}>
            {Array.from({ length: rows }).map((_, r) =>
                Array.from({ length }).map((_, i) => {
                    const offset = r % 2 === 0 ? 0 : 0.4;
                    return (
                        <mesh
                            key={`${r}-${i}`}
                            position={[-length / 2 + 0.45 + i * 0.9 + offset, 0.25 + r * 0.35, 0]}
                            rotation={[0, ((i + r) % 2) * 0.05, 0]}
                            castShadow={shadows}
                            receiveShadow
                            material={MAT.sand()}
                        >
                            <boxGeometry args={[0.9, 0.35, 0.5]} />
                        </mesh>
                    );
                })
            )}
        </group>
    );
}

// Tree (very low-poly)
export function Tree({ position, scale = 1, shadows = false }) {
    return (
        <group position={position} scale={scale}>
            {/* Trunk */}
            <mesh position={[0, 1.5, 0]} castShadow={shadows} material={MAT.trunk()}>
                <cylinderGeometry args={[0.18, 0.24, 3, 6]} />
            </mesh>
            {/* Canopy — stack of scaled cones */}
            <mesh position={[0, 3.2, 0]} castShadow={shadows} material={MAT.foliage()}>
                <coneGeometry args={[1.5, 2.2, 8]} />
            </mesh>
            <mesh position={[0, 4.2, 0]} castShadow={shadows} material={MAT.foliage()}>
                <coneGeometry args={[1.1, 1.8, 8]} />
            </mesh>
            <mesh position={[0, 5.0, 0]} castShadow={shadows} material={MAT.foliage()}>
                <coneGeometry args={[0.7, 1.2, 8]} />
            </mesh>
        </group>
    );
}

// Palm tree — desert
export function Palm({ position, scale = 1, shadows = false }) {
    return (
        <group position={position} scale={scale}>
            <mesh position={[0, 2, 0]} castShadow={shadows} material={MAT.trunk()}>
                <cylinderGeometry args={[0.16, 0.22, 4, 6]} />
            </mesh>
            {/* Fronds */}
            {[0, 60, 120, 180, 240, 300].map((deg, i) => (
                <mesh
                    key={i}
                    position={[0, 4, 0]}
                    rotation={[0.5, (deg * Math.PI) / 180, 0]}
                    material={MAT.foliage()}
                >
                    <boxGeometry args={[0.1, 0.06, 1.8]} />
                </mesh>
            ))}
        </group>
    );
}

// Window frame — placed on a wall face
export function Window({ position, rotation = [0, 0, 0], size = [1.6, 1.2] }) {
    const [w, h] = size;
    const frame = 0.08;
    return (
        <group position={position} rotation={rotation}>
            {/* Glass pane */}
            <mesh material={MAT.glass()}>
                <boxGeometry args={[w, h, 0.03]} />
            </mesh>
            {/* Frame */}
            <mesh position={[0, h / 2, 0]} material={MAT.metalDark()}>
                <boxGeometry args={[w + frame, frame, 0.05]} />
            </mesh>
            <mesh position={[0, -h / 2, 0]} material={MAT.metalDark()}>
                <boxGeometry args={[w + frame, frame, 0.05]} />
            </mesh>
            <mesh position={[-w / 2, 0, 0]} material={MAT.metalDark()}>
                <boxGeometry args={[frame, h + frame, 0.05]} />
            </mesh>
            <mesh position={[w / 2, 0, 0]} material={MAT.metalDark()}>
                <boxGeometry args={[frame, h + frame, 0.05]} />
            </mesh>
            {/* Cross bar */}
            <mesh material={MAT.metalDark()}>
                <boxGeometry args={[0.04, h, 0.04]} />
            </mesh>
        </group>
    );
}

// Road-marking stripe (dashed centre-line)
export function RoadStripe({ position, rotation = [0, 0, 0], length = 10 }) {
    return (
        <group position={[position[0], position[1] + 0.01, position[2]]} rotation={rotation}>
            {Array.from({ length: Math.floor(length / 2) }).map((_, i) => (
                <mesh
                    key={i}
                    position={[-length / 2 + 0.5 + i * 2, 0, 0]}
                    rotation={[-Math.PI / 2, 0, 0]}
                    material={MAT.asphaltMark()}
                >
                    <planeGeometry args={[1, 0.14]} />
                </mesh>
            ))}
        </group>
    );
}

// Simple building — a hollow box with door and roof
export function Building({
    position,
    rotation = [0, 0, 0],
    size = [8, 5, 6],
    color = "brick",
    withWindows = true,
    shadows = false,
}) {
    const [sx, sy, sz] = size;
    return (
        <group position={position} rotation={rotation}>
            {/* Walls (solid box for perf) */}
            <mesh castShadow={shadows} receiveShadow material={MAT[color]()}>
                <boxGeometry args={[sx, sy, sz]} />
            </mesh>
            {/* Flat roof cap */}
            <mesh position={[0, sy / 2 + 0.06, 0]} material={MAT.concreteDark()}>
                <boxGeometry args={[sx + 0.2, 0.12, sz + 0.2]} />
            </mesh>
            {/* Windows on front/back */}
            {withWindows && (
                <>
                    <Window position={[-sx / 4, 0, sz / 2 + 0.03]} size={[1.4, 1.2]} />
                    <Window position={[sx / 4, 0, sz / 2 + 0.03]} size={[1.4, 1.2]} />
                    <Window position={[-sx / 4, 0, -sz / 2 - 0.03]} rotation={[0, Math.PI, 0]} size={[1.4, 1.2]} />
                    <Window position={[sx / 4, 0, -sz / 2 - 0.03]} rotation={[0, Math.PI, 0]} size={[1.4, 1.2]} />
                </>
            )}
            {/* Door — dark metal panel */}
            <mesh position={[0, -sy / 2 + 1.2, sz / 2 + 0.03]} material={MAT.metalDark()}>
                <boxGeometry args={[1.1, 2.2, 0.05]} />
            </mesh>
        </group>
    );
}

// Small target dummy — training yard shooting target
export function TargetDummy({ position, rotation = [0, 0, 0] }) {
    return (
        <group position={position} rotation={rotation}>
            {/* Stand */}
            <mesh position={[0, 0.6, 0]} material={MAT.metalDark()}>
                <cylinderGeometry args={[0.05, 0.05, 1.2, 6]} />
            </mesh>
            <mesh position={[0, 0.05, 0]} material={MAT.metalDark()}>
                <boxGeometry args={[0.5, 0.1, 0.5]} />
            </mesh>
            {/* Silhouette */}
            <mesh position={[0, 1.5, 0]} material={MAT.woodLight()}>
                <boxGeometry args={[0.8, 1.2, 0.05]} />
            </mesh>
            {/* Bullseye */}
            <mesh position={[0, 1.5, 0.03]} material={MAT.metalRed()}>
                <cylinderGeometry args={[0.25, 0.25, 0.02, 12]} rotation={[Math.PI / 2, 0, 0]} />
            </mesh>
        </group>
    );
}

// Sign board / hazard warning
export function Sign({ position, rotation = [0, 0, 0], color = "signBlue", text = "" }) {
    return (
        <group position={position} rotation={rotation}>
            <mesh position={[0, 1, 0]} material={MAT.metalDark()}>
                <cylinderGeometry args={[0.04, 0.04, 2, 6]} />
            </mesh>
            <mesh position={[0, 2, 0]} material={MAT[color]()}>
                <boxGeometry args={[1.2, 0.6, 0.06]} />
            </mesh>
        </group>
    );
}

// Metal pipe (industrial)
export function Pipe({ position, rotation = [0, 0, 0], length = 6, radius = 0.18 }) {
    return (
        <group position={position} rotation={rotation}>
            <mesh material={MAT.metalRust()}>
                <cylinderGeometry args={[radius, radius, length, 10]} />
            </mesh>
            {/* Flanges each end */}
            {[-length / 2, length / 2].map((y, i) => (
                <mesh key={i} position={[0, y, 0]} material={MAT.metalDark()}>
                    <cylinderGeometry args={[radius * 1.4, radius * 1.4, 0.12, 10]} />
                </mesh>
            ))}
        </group>
    );
}

// Concrete floor / tarmac plane at y=0 (per-map)
export function Ground({ material = "asphalt", size = 120 }) {
    return (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow material={MAT[material]()}>
            <planeGeometry args={[size, size]} />
        </mesh>
    );
}

// Ground with strong grid overlay for training map
export function TrainingFloor({ size = 60 }) {
    return (
        <group>
            <Ground material="concreteDark" size={size} />
            <mesh
                rotation={[-Math.PI / 2, 0, 0]}
                position={[0, 0.005, 0]}
            >
                <planeGeometry args={[size * 0.9, size * 0.9]} />
                <meshStandardMaterial
                    color="#3a3a3a"
                    roughness={0.9}
                    metalness={0.0}
                    transparent
                    opacity={0.6}
                />
            </mesh>
        </group>
    );
}
